import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { normalizeStatusValue, normalizeLeaveTypeValue, normalizeInfinityCodeString } from "../utils/normalizeUtils";

export const useLeaveRequests = ({
  profile,
  language,
  filterStatus,
  filterType,
  searchTerm,
  decision,
  comments,
  selectedLeave,
  onDecisionSuccess,
  onAlert,
}) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  // Starts true (not false) so the page-level skeleton is guaranteed to be visible
  // on first paint instead of racing the mount effect that flips it.
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefreshEnabled] = useState(true);

  const getCurrentViewType = () => {
    const storedManagerViewType = localStorage.getItem("managerViewType");
    const storedViewType = localStorage.getItem("viewType");
    return String(storedManagerViewType || storedViewType || "admin").toLowerCase();
  };

  const calculateStats = (leaves) => {
    setStats({
      pending: leaves.filter((l) => l.status === "pending").length,
      approved: leaves.filter((l) => l.status === "approved").length,
      rejected: leaves.filter((l) => l.status === "rejected").length,
      total: leaves.length,
    });
  };

  const applyFilters = (leaves) => {
    let filtered = leaves.map((leave) => ({
      ...leave,
      status: normalizeStatusValue(leave.status),
      leaveType: normalizeLeaveTypeValue(leave.leaveType || leave.type),
    }));

    if (filterStatus !== "all") {
      filtered = filtered.filter((l) => l.status === filterStatus);
    }
    if (filterType !== "all") {
      filtered = filtered.filter((l) => (l.leaveType || l.type) === filterType);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          (l.employeeName || "").toLowerCase().includes(q) ||
          (l.reason || "").toLowerCase().includes(q)
      );
    }

    setFilteredRequests(filtered.filter((l) => (l.leaveType || l.type) !== "wfh"));
    setWfhRequests(filtered.filter((l) => (l.leaveType || l.type) === "wfh"));
  };

  const fetchLeaveRequests = async (silentRefresh = false) => {
    if (!silentRefresh) setIsLoading(true);
    else setIsAutoRefreshing(true);
    setError(null);

    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const storedInfinityCode = normalizeInfinityCodeString(
        profile?.infinityCode ?? localStorage.getItem("infinityCode")
      );
      const storedViewType = (localStorage.getItem("viewType") || "").toLowerCase();
      const myAdminQuery = storedInfinityCode
        ? `myAdmin=${encodeURIComponent(storedInfinityCode)}`
        : "";

      let url;
      if (storedViewType === "admin") {
        url = `${BASE_URL}/api/leaves/visibility${clientName ? `?clientName=${encodeURIComponent(clientName)}` : ""}`;
      } else if (clientName && clientName !== "Infinity") {
        url = `${BASE_URL}/api/leaves/client/${encodeURIComponent(clientName)}${myAdminQuery ? `?${myAdminQuery}` : ""}`;
      } else {
        url = `${BASE_URL}/api/leaves${myAdminQuery ? `?${myAdminQuery}` : ""}`;
      }

      const response = await apiFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.success && result.data?.leaves) {
        const leaves = result.data.leaves.map((leave) => ({
          ...leave,
          status: normalizeStatusValue(leave.status || leave.currentStatus || leave.State || leave.Status),
          leaveType: normalizeLeaveTypeValue(leave.leaveType || leave.type || leave.leave_type || leave.LeaveType),
        }));
        setLeaveRequests(leaves);
        calculateStats(leaves);
        applyFilters(leaves);
        setLastRefresh(new Date());
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching leave requests:", err);
      if (!silentRefresh) {
        setError(err.message);
        setLeaveRequests([]);
        setFilteredRequests([]);
        setWfhRequests([]);
      }
    } finally {
      setIsLoading(false);
      setIsAutoRefreshing(false);
    }
  };

  const handleSubmitDecision = async () => {
    if (!decision || !selectedLeave) {
      onAlert({ open: true, title: language === "ar" ? "تنبيه" : "Alert", message: language === "ar" ? "الرجاء اختيار موافقة أو رفض" : "Please select approve or reject", variant: "error" });
      return;
    }
    setIsSubmitting(true);
    try {
      const currentViewType = getCurrentViewType();
      const payload = {
        status: decision,
        approvedBy: profile?.employeeName || localStorage.getItem("employeeName") || "Admin",
        comments: comments.trim(),
      };
      const url = `${BASE_URL}/api/leaves/${selectedLeave.id}?viewType=${encodeURIComponent(currentViewType)}`;
      const response = await apiFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-View-Type": currentViewType },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) {
        onAlert({
          open: true,
          title: language === "ar" ? "نجاح" : "Success",
          message: language === "ar"
            ? `تم ${decision === "approved" ? "الموافقة على" : "رفض"} طلب الإجازة بنجاح!`
            : `Leave request ${decision} successfully!`,
          variant: "success",
        });
        await fetchLeaveRequests();
        onDecisionSuccess();
      } else {
        throw new Error(result.error || result.message || "Failed to submit decision");
      }
    } catch (err) {
      onAlert({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? `فشل إرسال القرار: ${err.message}` : `Failed to submit decision: ${err.message}`,
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initial load + auto-refresh
  useEffect(() => {
    fetchLeaveRequests(false);
    if (!autoRefreshEnabled) return;
    const id = setInterval(() => fetchLeaveRequests(true), 54000000);
    return () => clearInterval(id);
  }, [autoRefreshEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply filters when dependencies change
  useEffect(() => {
    if (leaveRequests.length > 0) applyFilters(leaveRequests);
  }, [searchTerm, filterStatus, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    leaveRequests,
    filteredRequests,
    wfhRequests,
    stats,
    isLoading,
    isAutoRefreshing,
    isSubmitting,
    error,
    lastRefresh,
    fetchLeaveRequests,
    handleSubmitDecision,
  };
};